import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { SRT_LOGO } from "@/lib/assets";

/**
 * SRT Finder brand mark. Modern 5-spoke forged performance wheel with a red
 * anodized rim lip and glowing hub (SVG-rendered), alongside the wordmark.
 *
 * Sizes: xs · sm · md · lg · xl
 */
export default function Logo({ size = "md", className = "", linkTo = "/" }) {
    const wheelDim =
        size === "xl" ? 64 : size === "lg" ? 50 : size === "sm" ? 32 : size === "xs" ? 24 : 40;
    const wordmarkHeight =
        size === "xl" ? 60 : size === "lg" ? 50 : size === "sm" ? 34 : size === "xs" ? 26 : 42;

    return (
        <Link
            to={linkTo}
            data-testid="srt-logo"
            className={`flex items-center gap-2.5 ${className}`}
            aria-label="SRT Finder — home"
        >
            <ForgedWheel size={wheelDim} />
            <img
                src={SRT_LOGO}
                alt="SRT Finder"
                className="select-none block"
                style={{ height: wordmarkHeight, width: "auto" }}
                draggable={false}
            />
        </Link>
    );
}

/**
 * Realistic stock Dodge Charger SRT "Slingshot" 20" wheel — 5 split-spoke
 * (10-arm) design in graphite. SVG so it stays crisp at any size.
 * Chunky sidewall + a touch of dirt flicking off the tread.
 */
export function ForgedWheel({ size = 40, spin = true, className = "" }) {
    const id = React.useId();
    // Wheel geometry (viewBox 100x100) — bigger tire, rim proportion preserved
    const cx = 50;
    const cy = 50;
    const rTire = 49;
    const rRim = 37;    // outer rim edge (was 42) — thicker sidewall
    const rFace = 35;   // wheel face
    const rHub = 7.5;   // center cap
    const rBoltCircle = 12.5;
    const spokeOuter = rFace - 0.6;  // where spoke tips meet the rim lip
    const spokeInner = rHub + 0.6;   // where spokes root into the hub

    // Slingshot split-spoke petal (pointing up, rotated 5x). Graphite palette.
    const spokePath = `
        M ${cx - 5.6} ${cy - spokeOuter}
        Q ${cx - 3.6} ${cy - (spokeOuter - 6)} ${cx - 1.9} ${cy - spokeInner}
        L ${cx - 0.75} ${cy - spokeInner + 0.6}
        L ${cx - 0.75} ${cy - (spokeOuter - 12)}
        Q ${cx - 0.35} ${cy - (spokeOuter - 6)} ${cx} ${cy - spokeOuter + 0.4}
        Q ${cx + 0.35} ${cy - (spokeOuter - 6)} ${cx + 0.75} ${cy - (spokeOuter - 12)}
        L ${cx + 0.75} ${cy - spokeInner + 0.6}
        L ${cx + 1.9} ${cy - spokeInner}
        Q ${cx + 3.6} ${cy - (spokeOuter - 6)} ${cx + 5.6} ${cy - spokeOuter}
        Q ${cx + 3.7} ${cy - spokeOuter + 2.2} ${cx} ${cy - spokeOuter + 2.4}
        Q ${cx - 3.7} ${cy - spokeOuter + 2.2} ${cx - 5.6} ${cy - spokeOuter}
        Z
    `;

    return (
        <div
            className={`relative shrink-0 ${className}`}
            style={{
                width: size,
                height: size,
                filter: "drop-shadow(0 5px 12px rgba(0,0,0,0.7))",
                overflow: "visible",
            }}
        >
            <motion.svg
                width={size}
                height={size}
                viewBox="0 0 100 100"
                animate={spin ? { rotate: 360 } : { rotate: 0 }}
                transition={
                    spin
                        ? { repeat: Infinity, duration: 3.4, ease: "linear" }
                        : undefined
                }
                style={{ display: "block", position: "relative", overflow: "visible" }}
            >
                <defs>
                    {/* Tire rubber — pure black street tread */}
                    <radialGradient id={`${id}-tire`} cx="50%" cy="50%" r="50%">
                        <stop offset="70%" stopColor="#0a0a0a" />
                        <stop offset="88%" stopColor="#050505" />
                        <stop offset="100%" stopColor="#000" />
                    </radialGradient>
                    {/* Tire sidewall highlight — thin gloss band */}
                    <linearGradient id={`${id}-sidewall`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2e2e2e" stopOpacity="0.7" />
                        <stop offset="50%" stopColor="#0a0a0a" stopOpacity="0" />
                        <stop offset="100%" stopColor="#2a2a2a" stopOpacity="0.5" />
                    </linearGradient>
                    {/* Graphite rim lip — no bright silver flash */}
                    <radialGradient id={`${id}-lip`} cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#1a1c20" stopOpacity="0" />
                        <stop offset="80%" stopColor="#1a1c20" stopOpacity="0" />
                        <stop offset="90%" stopColor="#3f444a" />
                        <stop offset="96%" stopColor="#1e2126" />
                        <stop offset="100%" stopColor="#050608" />
                    </radialGradient>
                    {/* Wheel face — concave graphite, cool grey highlight top-left */}
                    <radialGradient id={`${id}-face`} cx="38%" cy="30%" r="70%">
                        <stop offset="0%" stopColor="#3d434b" />
                        <stop offset="40%" stopColor="#1f242a" />
                        <stop offset="80%" stopColor="#101317" />
                        <stop offset="100%" stopColor="#050608" />
                    </radialGradient>
                    {/* Spoke gloss — graphite, muted highlight */}
                    <linearGradient id={`${id}-spoke`} x1="0.3" y1="0" x2="0.7" y2="1">
                        <stop offset="0%" stopColor="#7a8188" />
                        <stop offset="14%" stopColor="#4a5058" />
                        <stop offset="45%" stopColor="#22262c" />
                        <stop offset="80%" stopColor="#0f1216" />
                        <stop offset="100%" stopColor="#05070a" />
                    </linearGradient>
                    {/* Center cap — graphite dome */}
                    <radialGradient id={`${id}-hub`} cx="35%" cy="30%" r="70%">
                        <stop offset="0%" stopColor="#3a3f46" />
                        <stop offset="55%" stopColor="#12151a" />
                        <stop offset="100%" stopColor="#000" />
                    </radialGradient>
                    {/* Center red — smaller, less "far", contained */}
                    <radialGradient id={`${id}-hub-red`} cx="35%" cy="30%" r="65%">
                        <stop offset="0%" stopColor="#ff8f8a" />
                        <stop offset="45%" stopColor="#c00500" />
                        <stop offset="100%" stopColor="#3a0100" />
                    </radialGradient>
                    {/* Faint caliper glow, graphite-shifted */}
                    <radialGradient id={`${id}-caliper`} cx="50%" cy="52%" r="45%">
                        <stop offset="0%" stopColor="#8a1410" stopOpacity="0.75" />
                        <stop offset="60%" stopColor="#3a0800" stopOpacity="0.4" />
                        <stop offset="100%" stopColor="#000" stopOpacity="0" />
                    </radialGradient>
                    {/* Dust particle radial */}
                    <radialGradient id={`${id}-dust`} cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#8a7b6a" stopOpacity="0.9" />
                        <stop offset="60%" stopColor="#5a4f42" stopOpacity="0.35" />
                        <stop offset="100%" stopColor="#3a3128" stopOpacity="0" />
                    </radialGradient>
                </defs>

                {/* Tire — smooth black sidewall */}
                <circle cx={cx} cy={cy} r={rTire} fill={`url(#${id}-tire)`} />
                {/* Sidewall sheen ring */}
                <circle
                    cx={cx}
                    cy={cy}
                    r={rTire - 1}
                    fill="none"
                    stroke={`url(#${id}-sidewall)`}
                    strokeWidth="1.2"
                />
                {/* Two subtle circumferential grooves — street tread */}
                <circle
                    cx={cx}
                    cy={cy}
                    r={rTire - 1.6}
                    fill="none"
                    stroke="#050505"
                    strokeOpacity="0.9"
                    strokeWidth="0.35"
                />
                <circle
                    cx={cx}
                    cy={cy}
                    r={rTire - 3.2}
                    fill="none"
                    stroke="#050505"
                    strokeOpacity="0.75"
                    strokeWidth="0.28"
                />
                {/* Fine radial siping — hairline "cuts" every ~10° for a real street-tire feel */}
                {Array.from({ length: 36 }).map((_, i) => {
                    const deg = i * 10;
                    const rad = (deg * Math.PI) / 180;
                    const rOuter = rTire - 1.4;
                    const rInner = rTire - 3.6;
                    const x1 = cx + Math.cos(rad) * rOuter;
                    const y1 = cy + Math.sin(rad) * rOuter;
                    const x2 = cx + Math.cos(rad) * rInner;
                    const y2 = cy + Math.sin(rad) * rInner;
                    return (
                        <line
                            key={`sipe-${deg}`}
                            x1={x1}
                            y1={y1}
                            x2={x2}
                            y2={y2}
                            stroke="#040404"
                            strokeOpacity="0.85"
                            strokeWidth="0.28"
                            strokeLinecap="round"
                        />
                    );
                })}
                {/* Inner sidewall shadow — sells the rubber depth */}
                <circle
                    cx={cx}
                    cy={cy}
                    r={rRim + 1}
                    fill="none"
                    stroke="#000"
                    strokeOpacity="0.75"
                    strokeWidth="0.9"
                />

                {/* Graphite rim lip */}
                <circle cx={cx} cy={cy} r={rRim} fill={`url(#${id}-lip)`} />
                {/* Barrel shadow */}
                <circle
                    cx={cx}
                    cy={cy + 0.7}
                    r={rFace + 0.5}
                    fill="none"
                    stroke="#000"
                    strokeOpacity="0.55"
                    strokeWidth="1.1"
                />

                {/* Concave face */}
                <circle cx={cx} cy={cy} r={rFace} fill={`url(#${id}-face)`} />

                {/* Muted caliper hint behind spokes */}
                <g opacity="0.8">
                    <ellipse
                        cx={cx + 10}
                        cy={cy + 5}
                        rx="8"
                        ry="5"
                        fill={`url(#${id}-caliper)`}
                        transform={`rotate(-25 ${cx + 10} ${cy + 5})`}
                    />
                </g>

                {/* Slingshot 5 split-spokes */}
                {[0, 72, 144, 216, 288].map((deg) => (
                    <g key={deg} transform={`rotate(${deg} ${cx} ${cy})`}>
                        <path
                            d={spokePath}
                            fill={`url(#${id}-spoke)`}
                            stroke="#000"
                            strokeOpacity="0.7"
                            strokeWidth="0.32"
                        />
                        {/* Subtle top-edge kiss */}
                        <path
                            d={`M ${cx - 4.5} ${cy - spokeOuter + 1.2} Q ${cx} ${cy - spokeOuter + 3} ${cx + 4.5} ${cy - spokeOuter + 1.2}`}
                            stroke="#9aa2ab"
                            strokeOpacity="0.35"
                            strokeWidth="0.4"
                            fill="none"
                            strokeLinecap="round"
                        />
                    </g>
                ))}

                {/* Lug bolts */}
                {[36, 108, 180, 252, 324].map((deg) => {
                    const rad = (deg * Math.PI) / 180;
                    const x = cx + Math.cos(rad - Math.PI / 2) * rBoltCircle;
                    const y = cy + Math.sin(rad - Math.PI / 2) * rBoltCircle;
                    return (
                        <g key={`lug-${deg}`}>
                            <circle
                                cx={x}
                                cy={y}
                                r="1.2"
                                fill="#0a0b0d"
                                stroke="#2f333a"
                                strokeWidth="0.3"
                            />
                            <circle
                                cx={x - 0.3}
                                cy={y - 0.3}
                                r="0.3"
                                fill="#b6bbc2"
                                opacity="0.55"
                            />
                        </g>
                    );
                })}

                {/* Center cap */}
                <circle
                    cx={cx}
                    cy={cy}
                    r={rHub}
                    fill={`url(#${id}-hub)`}
                    stroke="#000"
                    strokeWidth="0.5"
                />
                <circle cx={cx} cy={cy} r={rHub - 3} fill={`url(#${id}-hub-red)`} />
                <ellipse
                    cx={cx - 1.6}
                    cy={cy - 2.2}
                    rx="3.2"
                    ry="1.2"
                    fill="#ffffff"
                    opacity="0.22"
                />

                {/* Broad graphite face gloss sweep */}
                <path
                    d={`M ${cx - 28} ${cy - 10}
                        A 30 30 0 0 1 ${cx + 20} ${cy - 22}`}
                    stroke="#a9b0b8"
                    strokeOpacity="0.10"
                    strokeWidth="1.4"
                    fill="none"
                    strokeLinecap="round"
                />
            </motion.svg>

            {/* Dust flick — counter-rotates so it visually detaches from the wheel */}
            <motion.svg
                width={size}
                height={size}
                viewBox="0 0 100 100"
                className="absolute inset-0 pointer-events-none"
                style={{ overflow: "visible" }}
                animate={spin ? { rotate: -360 } : { rotate: 0 }}
                transition={
                    spin
                        ? { repeat: Infinity, duration: 3.4, ease: "linear" }
                        : undefined
                }
            >
                <defs>
                    <radialGradient id={`${id}-dust-p`} cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#9c8a76" stopOpacity="0.85" />
                        <stop offset="60%" stopColor="#5a4f42" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="#3a3128" stopOpacity="0" />
                    </radialGradient>
                </defs>
                {/* Bottom-right dirt spray */}
                {[
                    { x: 78, y: 82, r: 2.6, delay: 0 },
                    { x: 86, y: 76, r: 1.6, delay: 0.4 },
                    { x: 90, y: 88, r: 2.0, delay: 0.9 },
                    { x: 72, y: 92, r: 1.4, delay: 1.3 },
                    { x: 82, y: 94, r: 1.2, delay: 1.7 },
                ].map((p, i) => (
                    <motion.circle
                        key={i}
                        cx={p.x}
                        cy={p.y}
                        r={p.r}
                        fill={`url(#${id}-dust-p)`}
                        initial={{ opacity: 0, scale: 0.6, x: 0, y: 0 }}
                        animate={{
                            opacity: [0, 0.9, 0],
                            scale: [0.6, 1, 1.35],
                            x: [0, 4, 10],
                            y: [0, 3, 7],
                        }}
                        transition={{
                            duration: 2.2,
                            delay: p.delay,
                            repeat: Infinity,
                            ease: "easeOut",
                        }}
                    />
                ))}
            </motion.svg>
        </div>
    );
}

/** Standalone spinning wheel mark — used where the wordmark isn't needed. */
export function LogoMark({ size = 28 }) {
    return <ForgedWheel size={size} />;
}
